import { useGetConversationHistoryQuery } from '@/services/ApiService';
import { cn } from '@/lib/utils';

interface ConversationItemProps {
    conversationId: string;
    isActive: boolean;
    onClick: () => void;
}

const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
};

const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength).trim() + '...';
};

export const ConversationHistoryItem = ({ conversationId, isActive, onClick }: ConversationItemProps) => {
    const { data: historyResponse, isLoading } = useGetConversationHistoryQuery(conversationId);

    const messages = historyResponse?.messages;
    const firstUserMessage = messages?.find(msg => msg.role === 'user');
    const lastMessage = messages?.length ? messages[messages.length - 1] : undefined;
    const title = firstUserMessage
        ? truncateText(firstUserMessage.content, 30)
        : 'New conversation';
    const timestamp = lastMessage?.createdAt;

    return (
        <button
            onClick={onClick}
            className={cn(
                'min-w-0 w-full text-left px-3 py-2 rounded-lg transition-colors',
                'hover:light:bg-gz-surface-medium dark:hover:bg-gz-blue-700',
                isActive ? 'light:bg-gz-surface-dark dark:bg-gz-blue-600' : 'bg-transparent',
            )}
            data-track-click="AI Chat Conversation Selected"
            data-track-location="ai-chat"
            data-track-button-name="ai-chat-conversation-item"
            data-track-conversation-id={conversationId}
        >
            {isLoading
                ? (
                        <div className="animate-pulse" role="status" aria-label="Loading">
                            <div className="h-4 bg-secondary/50 rounded w-3/4 mb-1" />
                            <div className="h-3 bg-secondary/50 rounded w-1/2" />
                        </div>
                    )
                : (
                        <>
                            <p className="rr-block text-sm text-white light:text-foreground dark:text-foreground truncate">{title}</p>
                            {timestamp && (
                                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                                    {formatRelativeTime(timestamp)}
                                </p>
                            )}
                        </>
                    )}
        </button>
    );
};
